/**
 * Payroll Service
 * 
 * Provides functionality to manage payroll periods in Firestore
 * Handles CRUD operations and dynamic status calculation
 * 
 * @module PayrollService
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { PayrollPeriod, PayrollPeriodInput, PayrollPeriodStatus } from '../@types/payroll';

export class PayrollService {
  private static readonly COLLECTION_NAME = 'payroll_periods';

  /**
   * Calculate payroll period status based on current date
   * 
   * @param period - Payroll period to calculate status for
   * @returns Calculated status
   */
  static calculateStatus(period: PayrollPeriod): PayrollPeriodStatus {
    const now = new Date();
    const startDate = period.startDate.toDate();
    const endDate = period.endDate.toDate();
    const submitDate = period.submitDate.toDate();
    const payDate = period.payDate.toDate();

    // If pay date or submit date has passed, period is completed
    if (now > payDate || now > submitDate) {
      return 'completed';
    }

    // If current date is within the pay period (between start and end), it's current
    if (now >= startDate && now <= endDate) {
      return 'current';
    }

    // If current date is before the pay period start, it's upcoming
    if (now < startDate) {
      return 'upcoming';
    }

    // If we're between end date and submit date, it's current (submission period)
    if (now > endDate && now <= submitDate) {
      return 'current';
    }

    // Default to upcoming
    return 'upcoming';
  }

  /**
   * Calculate days remaining until submit deadline
   * 
   * @param period - Payroll period
   * @returns Number of days remaining (can be negative if past deadline)
   */
  static calculateDaysRemaining(period: PayrollPeriod): number {
    const now = new Date();
    const submitDate = period.submitDate.toDate();
    const diffTime = submitDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Get all payroll periods
   * 
   * @param filters - Optional filters (e.g., status, date range)
   * @returns Array of payroll periods
   */
  static async getPayrollPeriods(filters?: {
    status?: PayrollPeriodStatus;
    startDate?: Date;
    endDate?: Date;
  }): Promise<PayrollPeriod[]> {
    try {
      const periodsRef = collection(db, this.COLLECTION_NAME);
      const constraints: QueryConstraint[] = [orderBy('startDate', 'asc')];

      // Apply filters if provided
      if (filters?.startDate) {
        constraints.push(where('startDate', '>=', Timestamp.fromDate(filters.startDate)));
      }
      if (filters?.endDate) {
        constraints.push(where('startDate', '<=', Timestamp.fromDate(filters.endDate)));
      }

      const q = query(periodsRef, ...constraints);
      const querySnapshot = await getDocs(q);
      
      let periods: PayrollPeriod[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PayrollPeriod));

      // Calculate status and days remaining for each period
      periods = periods.map(period => {
        const status = this.calculateStatus(period);
        const daysRemaining = this.calculateDaysRemaining(period);
        return {
          ...period,
          status,
          daysRemaining
        };
      });

      // Filter by status if provided (after calculating status)
      if (filters?.status) {
        periods = periods.filter(period => period.status === filters.status);
      }

      return periods;
    } catch (error: any) {
      console.error('[PayrollService] Error getting payroll periods:', error);
      throw new Error(`Failed to get payroll periods: ${error.message}`);
    }
  }

  /**
   * Get a single payroll period by ID
   * 
   * @param periodId - Payroll period document ID
   * @returns Payroll period or null if not found
   */
  static async getPayrollPeriod(periodId: string): Promise<PayrollPeriod | null> {
    try {
      const periodRef = doc(db, this.COLLECTION_NAME, periodId);
      const periodSnap = await getDoc(periodRef);

      if (!periodSnap.exists()) {
        return null;
      }

      const period = {
        id: periodSnap.id,
        ...periodSnap.data()
      } as PayrollPeriod;

      // Calculate status and days remaining
      const status = this.calculateStatus(period);
      const daysRemaining = this.calculateDaysRemaining(period);

      return {
        ...period,
        status,
        daysRemaining
      };
    } catch (error: any) {
      console.error('[PayrollService] Error getting payroll period:', error);
      throw new Error(`Failed to get payroll period: ${error.message}`);
    }
  }

  /**
   * Get current payroll period
   * 
   * @returns Current payroll period or null if none found
   */
  static async getCurrentPayrollPeriod(): Promise<PayrollPeriod | null> {
    try {
      const periods = await this.getPayrollPeriods();
      const current = periods.find(period => this.calculateStatus(period) === 'current');
      return current || null;
    } catch (error: any) {
      console.error('[PayrollService] Error getting current payroll period:', error);
      throw new Error(`Failed to get current payroll period: ${error.message}`);
    }
  }

  /**
   * Create a new payroll period
   * 
   * @param periodInput - Payroll period input data
   * @param userId - User ID creating the period
   * @returns Created payroll period with ID
   */
  static async createPayrollPeriod(
    periodInput: PayrollPeriodInput,
    userId: string
  ): Promise<PayrollPeriod> {
    try {
      // Validate dates
      const startDate = new Date(periodInput.startDate);
      const endDate = new Date(periodInput.endDate);
      const submitDate = new Date(periodInput.submitDate);
      const payDate = new Date(periodInput.payDate);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || 
          isNaN(submitDate.getTime()) || isNaN(payDate.getTime())) {
        throw new Error('Invalid date format. Dates must be in YYYY-MM-DD format.');
      }

      // Validate date order
      if (startDate >= endDate) {
        throw new Error('Start date must be before end date.');
      }
      if (endDate > submitDate) {
        throw new Error('End date must be before or equal to submit date.');
      }
      if (submitDate >= payDate) {
        throw new Error('Submit date must be before pay date.');
      }

      // Check for overlapping periods
      const existingPeriods = await this.getPayrollPeriods();
      const hasOverlap = existingPeriods.some(period => {
        const existingStart = period.startDate.toDate();
        const existingEnd = period.endDate.toDate();
        return (startDate <= existingEnd && endDate >= existingStart);
      });

      if (hasOverlap) {
        throw new Error('Payroll period overlaps with an existing period.');
      }

      // Check for duplicate period number
      const hasDuplicateNumber = existingPeriods.some(
        period => period.periodNumber === periodInput.periodNumber
      );

      if (hasDuplicateNumber) {
        throw new Error(`Period number ${periodInput.periodNumber} already exists.`);
      }

      const periodData: Omit<PayrollPeriod, 'id' | 'status' | 'daysRemaining'> = {
        periodNumber: periodInput.periodNumber,
        startDate: Timestamp.fromDate(startDate),
        endDate: Timestamp.fromDate(endDate),
        submitDate: Timestamp.fromDate(submitDate),
        payDate: Timestamp.fromDate(payDate),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: userId,
        updatedBy: userId
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), periodData);

      const createdPeriod: PayrollPeriod = {
        id: docRef.id,
        ...periodData,
        status: this.calculateStatus(periodData as PayrollPeriod),
        daysRemaining: this.calculateDaysRemaining(periodData as PayrollPeriod)
      };

      console.log('[PayrollService] Created payroll period:', docRef.id);
      return createdPeriod;
    } catch (error: any) {
      console.error('[PayrollService] Error creating payroll period:', error);
      throw error;
    }
  }

  /**
   * Update an existing payroll period
   * 
   * @param periodId - Payroll period document ID
   * @param periodInput - Updated payroll period data
   * @param userId - User ID updating the period
   * @returns Updated payroll period
   */
  static async updatePayrollPeriod(
    periodId: string,
    periodInput: Partial<PayrollPeriodInput>,
    userId: string
  ): Promise<PayrollPeriod> {
    try {
      const periodRef = doc(db, this.COLLECTION_NAME, periodId);
      const periodSnap = await getDoc(periodRef);

      if (!periodSnap.exists()) {
        throw new Error('Payroll period not found.');
      }

      const updateData: any = {
        updatedAt: Timestamp.now(),
        updatedBy: userId
      };

      // Convert date strings to Timestamps if provided
      if (periodInput.startDate) {
        const date = new Date(periodInput.startDate);
        if (isNaN(date.getTime())) {
          throw new Error('Invalid start date format.');
        }
        updateData.startDate = Timestamp.fromDate(date);
      }

      if (periodInput.endDate) {
        const date = new Date(periodInput.endDate);
        if (isNaN(date.getTime())) {
          throw new Error('Invalid end date format.');
        }
        updateData.endDate = Timestamp.fromDate(date);
      }

      if (periodInput.submitDate) {
        const date = new Date(periodInput.submitDate);
        if (isNaN(date.getTime())) {
          throw new Error('Invalid submit date format.');
        }
        updateData.submitDate = Timestamp.fromDate(date);
      }

      if (periodInput.payDate) {
        const date = new Date(periodInput.payDate);
        if (isNaN(date.getTime())) {
          throw new Error('Invalid pay date format.');
        }
        updateData.payDate = Timestamp.fromDate(date);
      }

      if (periodInput.periodNumber !== undefined) {
        updateData.periodNumber = periodInput.periodNumber;
      }

      await updateDoc(periodRef, updateData);

      // Get updated period
      const updatedPeriod = await this.getPayrollPeriod(periodId);
      if (!updatedPeriod) {
        throw new Error('Failed to retrieve updated payroll period.');
      }

      console.log('[PayrollService] Updated payroll period:', periodId);
      return updatedPeriod;
    } catch (error: any) {
      console.error('[PayrollService] Error updating payroll period:', error);
      throw error;
    }
  }

  /**
   * Delete a payroll period
   * 
   * @param periodId - Payroll period document ID
   */
  static async deletePayrollPeriod(periodId: string): Promise<void> {
    try {
      const periodRef = doc(db, this.COLLECTION_NAME, periodId);
      await deleteDoc(periodRef);
      console.log('[PayrollService] Deleted payroll period:', periodId);
    } catch (error: any) {
      console.error('[PayrollService] Error deleting payroll period:', error);
      throw new Error(`Failed to delete payroll period: ${error.message}`);
    }
  }

  /**
   * Bulk create payroll periods (useful for initial setup)
   * 
   * @param periodsInput - Array of payroll period inputs
   * @param userId - User ID creating the periods
   * @returns Array of created payroll periods
   */
  static async bulkCreatePayrollPeriods(
    periodsInput: PayrollPeriodInput[],
    userId: string
  ): Promise<PayrollPeriod[]> {
    try {
      const createdPeriods: PayrollPeriod[] = [];

      for (const periodInput of periodsInput) {
        const period = await this.createPayrollPeriod(periodInput, userId);
        createdPeriods.push(period);
      }

      console.log('[PayrollService] Bulk created', createdPeriods.length, 'payroll periods');
      return createdPeriods;
    } catch (error: any) {
      console.error('[PayrollService] Error bulk creating payroll periods:', error);
      throw error;
    }
  }
}

